import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import type { UnreadCounts } from "@/types";
import { SearchBar } from "@/components/search/SearchBar";

// Pages that render their own prominent search input above the fold; on these
// the navbar search hides until the page's search scrolls out of view.
const PAGE_SEARCH_THRESHOLDS: Record<string, number> = {
  "/": 380,
  "/listings": 200,
};

function useNavSearchHidden(): boolean {
  const { pathname } = useLocation();
  const threshold = PAGE_SEARCH_THRESHOLDS[pathname];
  const hasInPageSearch = threshold != null;
  const [hidden, setHidden] = useState(hasInPageSearch);

  useEffect(() => {
    if (!hasInPageSearch || threshold == null) {
      setHidden(false);
      return;
    }
    const update = () => setHidden(window.scrollY < threshold);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [hasInPageSearch, threshold]);

  return hidden;
}

const UNREAD_COUNTS_QUERY = gql`
  query UnreadCounts {
    unreadCounts {
      messages
      notifications
    }
  }
`;

export function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { data: counts } = useQuery({
    queryKey: ["unreadCounts"],
    queryFn: () =>
      graphqlClient.request<{ unreadCounts: UnreadCounts }>(
        UNREAD_COUNTS_QUERY
      ),
    enabled: !!user,
    refetchInterval: 30000,
  });

  const unread = counts?.unreadCounts;
  const hideSearch = useNavSearchHidden();

  return (
    <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <img src="/icon.svg" alt="Boardo" className="h-9 w-9" />
            <span className="hidden text-xl font-bold text-primary-800 sm:inline">
              Boardo
            </span>
          </Link>

          {/* Search — invisible (but space reserved) on homepage above the hero so nav links don't shift */}
          <div
            className={`hidden flex-1 transition-opacity duration-200 md:block ${
              hideSearch ? "pointer-events-none invisible" : "visible"
            }`}
            style={{ maxWidth: 480 }}
            aria-hidden={hideSearch || undefined}
          >
            <SearchBar />
          </div>

          {/* Nav links — lead with price tracking, demote marketplace */}
          <div className="hidden items-center gap-1 lg:flex">
            <Link
              to="/games"
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              {t("nav.games")}
            </Link>
            <Link
              to="/games?sort=biggest_drop"
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              {t("nav.deals")}
            </Link>
            <Link
              to="/listings"
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              {t("nav.marketplace")}
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link to="/listings/new" className="btn-primary hidden lg:flex">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Parduoti
                </Link>

                <Link
                  to="/notifications"
                  className="relative rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  {unread && unread.notifications > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {unread.notifications}
                    </span>
                  )}
                </Link>

                <Link
                  to="/messages"
                  className="relative rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  {unread && unread.messages > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {unread.messages}
                    </span>
                  )}
                </Link>

                {/* User dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-800 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
                  >
                    {user.displayName[0].toUpperCase()}
                  </button>
                  {dropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setDropdownOpen(false)}
                      />
                      <div className="absolute right-0 z-50 mt-2 w-56 rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                        <div className="border-b border-gray-100 px-4 py-3">
                          <p className="text-sm font-semibold text-gray-900">
                            {user.displayName}
                          </p>
                          <p className="truncate text-xs text-gray-500">
                            {user.email}
                          </p>
                        </div>
                        {user.admin && (
                          <Link
                            to="/admin"
                            onClick={() => setDropdownOpen(false)}
                            className="block px-4 py-2 text-sm font-medium text-primary-800 hover:bg-gray-50"
                          >
                            Admin
                          </Link>
                        )}
                        <Link
                          to="/my-listings"
                          onClick={() => setDropdownOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Mano skelbimai
                        </Link>
                        <Link
                          to="/wishlists"
                          onClick={() => setDropdownOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Mano stebėjimai
                        </Link>
                        <Link
                          to="/profile"
                          onClick={() => setDropdownOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {t("nav.my_account")}
                        </Link>
                        <div className="border-t border-gray-100">
                          <button
                            onClick={() => {
                              logout();
                              setDropdownOpen(false);
                              navigate("/");
                            }}
                            className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50"
                          >
                            Atsijungti
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost hidden sm:flex">
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  title={t("nav.register_hint")}
                  className="btn-primary"
                >
                  {t("nav.register")}
                </Link>
              </>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="rounded-lg p-2 text-gray-600 lg:hidden"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Persistent mobile search — also hidden on homepage above the hero */}
        {!hideSearch && (
          <div className="border-t border-gray-100 pb-3 pt-1 md:hidden">
            <SearchBar />
          </div>
        )}

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="border-t border-gray-200 pb-4 pt-2 lg:hidden">
            <Link
              to="/games"
              onClick={() => setMobileOpen(false)}
              className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              {t("nav.games")}
            </Link>
            <Link
              to="/games?sort=biggest_drop"
              onClick={() => setMobileOpen(false)}
              className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              {t("nav.deals")}
            </Link>
            <Link
              to="/listings"
              onClick={() => setMobileOpen(false)}
              className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100"
            >
              {t("nav.marketplace")}
            </Link>
            {user && (
              <>
                <Link
                  to="/listings/new"
                  onClick={() => setMobileOpen(false)}
                  className="mt-2 block rounded-lg bg-primary-800 px-3 py-2 text-center text-sm font-semibold text-white"
                >
                  Parduoti
                </Link>
                <div className="mt-3 border-t border-gray-200 pt-3">
                  {user.admin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-primary-800 hover:bg-gray-100"
                    >
                      Admin
                    </Link>
                  )}
                  <Link
                    to="/my-listings"
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Mano skelbimai
                  </Link>
                  <Link
                    to="/wishlists"
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Mano stebėjimai
                  </Link>
                  <Link
                    to="/messages"
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Žinutės
                    {unread && unread.messages > 0 && (
                      <span className="ml-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                        {unread.messages}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/notifications"
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Pranešimai
                    {unread && unread.notifications > 0 && (
                      <span className="ml-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                        {unread.notifications}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    {t("nav.my_account")}
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setMobileOpen(false);
                      navigate("/");
                    }}
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-red-600 hover:bg-gray-100"
                  >
                    Atsijungti
                  </button>
                </div>
              </>
            )}
            {!user && (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="mt-2 block rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Prisijungti
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
